import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { AlertTriangle, Check, ChevronDown, X } from 'lucide-react';

import { Button } from '@/components/Button';
import { cn } from '@/lib/cn';
import { fromLocalInput, toLocalInput } from '@/lib/datetimeInput';
import { motionSafe, springDefault } from '@/lib/motion';
import { useCurrentUser } from '@/features/auth/useAuth';
import { formatDuration } from '@/features/tasks/tree';
import { useProjects } from '@/features/tasks/useTasks';
import { useClientList, useProjectList } from '@/features/clients/useClientAdmin';
import { useUpdateItem } from './useBraindump';
import type { BraindumpItem } from './types';

interface Props {
  item: BraindumpItem;
  threshold: number;
  onAccept: () => void;
  onReject: () => void;
}

export function ReviewItem({ item, threshold, onAccept, onReject }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [title, setTitle] = useState(item.title);
  const update = useUpdateItem();

  const clients = useClientList();
  const projects = useProjectList();

  const decided = item.state !== 'proposed';
  // Flagged, not hidden and not silently trusted. A guess the model admits to
  // costs a glance; a confident wrong one costs a misfiled task.
  const uncertainClient = (item.client_confidence ?? 1) < threshold;
  const uncertainOverall = (item.confidence ?? 1) < threshold;

  const clientName = clients.data?.find((c) => c.id === item.client_id)?.name;
  const projectName = projects.data?.find((p) => p.id === item.project_id)?.name;

  const commitTitle = () => {
    if (title.trim() && title !== item.title) {
      update.mutate({ id: item.id, title: title.trim() });
    }
  };

  return (
    <li
      className={cn(
        'bg-surface-1 transition-colors duration-press',
        decided && 'opacity-50',
        uncertainOverall && !decided && 'bg-warning-quiet/30',
      )}
    >
      <div className="flex items-start gap-2 px-3 py-2.5">
        <div className="min-w-0 flex-1">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={commitTitle}
            disabled={decided}
            aria-label="Task title"
            className={cn(
              'w-full rounded border border-transparent bg-transparent px-1 py-0.5 -mx-1',
              'text-body outline-none focus-visible:ring-2 focus-visible:ring-ring',
              !decided && 'hover:border-border focus:border-accent-border',
              decided && 'line-through',
            )}
          />

          {/* The whole metadata line opens the editor. The chips are what you
              are looking at when you notice something is wrong, so they are
              what should respond. */}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-label="Edit client, project, estimate, and dates"
            disabled={decided}
            className={cn(
              '-mx-1 mt-1.5 flex w-full flex-wrap items-center gap-2 rounded px-1 py-0.5 text-left',
              'text-small text-text-tertiary transition-colors duration-press',
              !decided && 'hover:bg-surface-2',
            )}
          >
            {(
              <>
            {clientName ? (
              <span
                className={cn(
                  'rounded-md px-2 py-0.5 text-label',
                  uncertainClient
                    ? 'bg-warning-quiet text-warning'
                    : 'bg-surface-2 text-text-secondary',
                )}
              >
                {uncertainClient && (
                  <AlertTriangle size={10} strokeWidth={2} className="mr-1 inline" aria-hidden />
                )}
                {clientName}
              </span>
            ) : (
              // Unrouted is a state worth seeing, not an absence worth hiding.
              <span className="rounded-md bg-warning-quiet px-2 py-0.5 text-label text-warning">
                <AlertTriangle size={10} strokeWidth={2} className="mr-1 inline" aria-hidden />
                No client
              </span>
            )}
            {projectName && (
              <span className="rounded-md bg-surface-2 px-2 py-0.5 text-label text-text-secondary">
                {projectName}
              </span>
            )}
            {item.new_project_suggestion && (
              // Suggested, never created. A project invented behind your back
              // is a category you did not choose.
              <span className="rounded-md bg-accent-quiet px-2 py-0.5 text-label text-accent">
                new project: {item.new_project_suggestion}
              </span>
            )}
            {item.estimate_minutes && (
              <span className="tabular">{formatDuration(item.estimate_minutes)}</span>
            )}
            {item.priority && item.priority !== 'normal' && <span>{item.priority}</span>}
            {item.due_at && (
              <span className="tabular">
                due {new Date(item.due_at).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
            )}
            {item.subtasks && item.subtasks.length > 0 && (
              <span>{item.subtasks.length} subtasks</span>
            )}
              </>
            )}
          </button>
        </div>

        {!decided && (
          <div className="flex shrink-0 items-center gap-1">
            {/* Always present, not only when there is reasoning to read: this
                is where the routing gets corrected, and a fix you cannot find
                is a fix nobody makes. */}
            {(
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                aria-expanded={expanded}
                aria-label={expanded ? 'Hide details' : 'Edit details'}
                className="flex size-action items-center justify-center rounded-full text-text-tertiary hover:bg-surface-3 hover:text-text"
              >
                <motion.span
                  animate={{ rotate: expanded ? 180 : 0 }}
                  transition={motionSafe(springDefault)}
                  className="flex"
                >
                  <ChevronDown size={15} strokeWidth={1.5} aria-hidden />
                </motion.span>
              </button>
            )}
            <button
              type="button"
              onClick={onReject}
              aria-label={`Discard "${item.title}"`}
              className="pressable flex size-action items-center justify-center rounded-full text-text-tertiary hover:bg-surface-3 hover:text-danger"
            >
              <X size={15} strokeWidth={1.5} aria-hidden />
            </button>
            <button
              type="button"
              onClick={onAccept}
              aria-label={`Keep "${item.title}"`}
              className="pressable flex size-action items-center justify-center rounded-full bg-accent text-text-on-accent hover:bg-accent-hover"
            >
              <Check size={15} strokeWidth={2} aria-hidden />
            </button>
          </div>
        )}

        {item.state === 'accepted' && (
          <span className="shrink-0 text-label uppercase text-success">Added</span>
        )}
        {item.state === 'rejected' && (
          <span className="shrink-0 text-label uppercase text-text-tertiary">Discarded</span>
        )}
      </div>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={motionSafe(springDefault)}
            className="overflow-hidden"
          >
            <div className="border-t border-border px-3 py-3">
              {!decided && <ItemEditor item={item} />}

              {item.reasoning && (
                <p className={cn('text-small text-text-secondary', !decided && 'mt-3')}>
                  {item.reasoning}
                </p>
              )}
              {item.subtasks && item.subtasks.length > 0 && (
                <ul className="mt-2 flex flex-col gap-1">
                  {item.subtasks.map((sub, i) => (
                    <li key={i} className="flex justify-between gap-3 text-small text-text-secondary">
                      <span className="truncate">{sub.title}</span>
                      {sub.estimate_minutes && (
                        <span className="tabular shrink-0 text-text-tertiary">
                          {formatDuration(sub.estimate_minutes)}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}


const QUICK_ESTIMATES = [15, 30, 60, 120] as const;

const PRIORITIES = ['urgent', 'high', 'normal', 'low'] as const;
const ENERGIES = ['high', 'medium', 'low'] as const;

const SELECT_CLASS =
  'min-h-touch w-full rounded border border-border bg-surface-1 px-2 text-small text-text outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border';

/**
 * Correct a proposal before it becomes a task.
 *
 * Edits save straight to the item, not to a task -- nothing has been created
 * yet, and the point of the queue is that the record is still provisional.
 * Every field commits on change rather than behind a Save button, because the
 * next thing the user does is press Keep and a pending edit would be lost.
 */
function ItemEditor({ item }: { item: BraindumpItem }) {
  const update = useUpdateItem();
  const { data: user } = useCurrentUser();
  const timezone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const clients = useClientList();
  // Scoped to the chosen client: the server drops a project belonging to
  // anyone else, so offering one would be offering a silent no-op.
  const projects = useProjects(item.client_id ?? undefined);

  const set = (patch: Partial<BraindumpItem>) => update.mutate({ id: item.id, ...patch });

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div>
        <label
          htmlFor={`item-${item.id}-client`}
          className="mb-1.5 block text-small font-medium text-text-secondary"
        >
          Client
        </label>
        <select
          id={`item-${item.id}-client`}
          value={item.client_id ?? ''}
          onChange={(e) =>
            set({
              client_id: e.target.value ? Number(e.target.value) : null,
              // A project only makes sense under its own client; keeping it
              // here would send a pairing the server would silently discard.
              project_id: null,
            })
          }
          className={SELECT_CLASS}
        >
          <option value="">No client</option>
          {clients.data?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label
          htmlFor={`item-${item.id}-project`}
          className="mb-1.5 block text-small font-medium text-text-secondary"
        >
          Project
        </label>
        <select
          id={`item-${item.id}-project`}
          value={item.project_id ?? ''}
          disabled={!item.client_id}
          onChange={(e) =>
            set({ project_id: e.target.value ? Number(e.target.value) : null })
          }
          className={cn(SELECT_CLASS, !item.client_id && 'opacity-50')}
        >
          <option value="">No project</option>
          {projects.data?.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        {item.new_project_suggestion && (
          <p className="mt-1 text-label text-text-tertiary">
            Suggested: {item.new_project_suggestion}. Create it in Clients first to use it.
          </p>
        )}
      </div>

      <div className="sm:col-span-2">
        <label className="mb-1.5 block text-small font-medium text-text-secondary">
          Estimate
        </label>
        <div className="flex flex-wrap gap-1.5">
          {QUICK_ESTIMATES.map((minutes) => (
            <button
              key={minutes}
              type="button"
              onClick={() =>
                set({ estimate_minutes: item.estimate_minutes === minutes ? null : minutes })
              }
              aria-pressed={item.estimate_minutes === minutes}
              className={cn(
                'pressable tabular min-h-touch rounded-md px-3 text-small',
                item.estimate_minutes === minutes
                  ? 'bg-accent text-text-on-accent'
                  : 'bg-surface-2 text-text-secondary hover:bg-surface-3',
              )}
            >
              {formatDuration(minutes)}
            </button>
          ))}
          <input
            type="number"
            min={1}
            inputMode="numeric"
            defaultValue={item.estimate_minutes ?? ''}
            key={item.estimate_minutes ?? 'blank'}
            onBlur={(e) =>
              set({ estimate_minutes: e.target.value ? Number(e.target.value) : null })
            }
            aria-label="Estimate in minutes"
            placeholder="min"
            className="tabular min-h-touch w-20 rounded border border-border bg-surface-1 px-2 text-small outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border"
          />
        </div>
      </div>

      <div>
        <label
          htmlFor={`item-${item.id}-priority`}
          className="mb-1.5 block text-small font-medium text-text-secondary"
        >
          Priority
        </label>
        <select
          id={`item-${item.id}-priority`}
          value={item.priority ?? 'normal'}
          onChange={(e) => set({ priority: e.target.value })}
          className={SELECT_CLASS}
        >
          {PRIORITIES.map((value) => (
            <option key={value} value={value}>
              {value[0]!.toUpperCase() + value.slice(1)}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label
          htmlFor={`item-${item.id}-energy`}
          className="mb-1.5 block text-small font-medium text-text-secondary"
        >
          Energy
        </label>
        <select
          id={`item-${item.id}-energy`}
          value={item.energy ?? 'medium'}
          onChange={(e) => set({ energy: e.target.value })}
          className={SELECT_CLASS}
        >
          {ENERGIES.map((value) => (
            <option key={value} value={value}>
              {value[0]!.toUpperCase() + value.slice(1)}
            </option>
          ))}
        </select>
      </div>

      <div className="sm:col-span-2">
        <label
          htmlFor={`item-${item.id}-due`}
          className="mb-1.5 block text-small font-medium text-text-secondary"
        >
          Due
        </label>
        <input
          id={`item-${item.id}-due`}
          type="datetime-local"
          value={toLocalInput(item.due_at, timezone)}
          onChange={(e) => set({ due_at: fromLocalInput(e.target.value, timezone) })}
          className={cn(SELECT_CLASS, 'tabular')}
        />
      </div>
    </div>
  );
}

export function AcceptAllBar({
  pending,
  confident,
  onAcceptAll,
  onAcceptConfident,
  onRejectAll,
  busy,
}: {
  pending: number;
  confident: number;
  onAcceptAll: () => void;
  onAcceptConfident: () => void;
  onRejectAll: () => void;
  busy: boolean;
}) {
  if (pending === 0) return null;

  return (
    <div className="flex flex-col gap-2 border-b border-border px-3 py-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
      <p className="text-small text-text-secondary">
        {pending} {pending === 1 ? 'task' : 'tasks'}
      </p>
      <div className="grid grid-cols-2 gap-1.5 sm:flex sm:flex-wrap">
        <Button variant="ghost" onClick={onRejectAll} loading={busy} className="min-h-touch px-2.5">
          Discard all
        </Button>
        {confident > 0 && confident < pending && (
          <Button
            variant="secondary"
            onClick={onAcceptConfident}
            loading={busy}
            className="min-h-touch px-2.5 col-span-2 sm:col-span-1"
          >
            Keep {confident} confident
          </Button>
        )}
        <Button
          variant="primary"
          onClick={onAcceptAll}
          loading={busy}
          className={cn(
            'min-h-touch px-2.5',
            !(confident > 0 && confident < pending) && 'col-span-2 sm:col-span-1',
          )}
        >
          Keep all
        </Button>
      </div>
    </div>
  );
}
